import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const HEALTH_BADGE = {
  healthy:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning:  "bg-amber-100 text-amber-700 border-amber-200",
  degraded: "bg-orange-100 text-orange-700 border-orange-200",
  critical: "bg-red-100 text-red-700 border-red-200",
  unknown:  "bg-gray-100 text-gray-600 border-gray-200",
};

const GATE_MSG = {
  503: "V2 Runtime desativado",
  403: "Tenant não autorizado",
  404: "Endpoint V2 não disponível",
};

function GateError({ status }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
      {GATE_MSG[status] || "V2 indisponível"}
    </div>
  );
}

/**
 * V2 Runtime section for the tenant drilldown page.
 * Calls /whatsapp/v2/runtime/health/{tenantId} and /whatsapp/v2/runtime/recovery-advisory/{tenantId}.
 * Fail-soft: each fetch independent; gate errors inline; never crashes the page.
 * No sensitive data (no QR raw, no token, no session, no credentials).
 */
export default function V2TenantSection({ tenantId }) {
  const { authAxios } = useAuth();
  const [health, setHealth]     = useState(null);
  const [advisory, setAdvisory] = useState(null);
  const [healthErr, setHealthErr]   = useState(null);
  const [advErr, setAdvErr]         = useState(null);
  const [loading, setLoading]       = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [hRes, aRes] = await Promise.allSettled([
      authAxios.get(`/whatsapp/v2/runtime/health/${tenantId}`),
      authAxios.get(`/whatsapp/v2/runtime/recovery-advisory/${tenantId}`),
    ]);
    if (hRes.status === "fulfilled") { setHealth(hRes.value.data); setHealthErr(null); }
    else setHealthErr(hRes.reason?.response?.status || 0);
    if (aRes.status === "fulfilled") { setAdvisory(aRes.value.data); setAdvErr(null); }
    else setAdvErr(aRes.reason?.response?.status || 0);
    setLoading(false);
  }, [authAxios, tenantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar V2 Runtime…
      </div>
    );
  }

  const sig = health?.signal?.toLowerCase();

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-gray-800">V2 Runtime</h2>

      {healthErr
        ? <GateError status={healthErr} />
        : health && (
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Health</span>
                <Badge className={`text-xs border ${HEALTH_BADGE[sig] || HEALTH_BADGE.unknown}`}>
                  {health.signal || "UNKNOWN"}
                </Badge>
              </div>
              {health.confidence != null && (
                <p className="text-xs text-muted-foreground">Confidence: {health.confidence}</p>
              )}
              {health.warnings?.length > 0 && (
                <ul className="text-xs text-amber-700 space-y-0.5">
                  {health.warnings.slice(0, 5).map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              )}
            </CardContent>
          </Card>
        )
      }

      {advErr
        ? <GateError status={advErr} />
        : advisory && (
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Recovery Advisory</span>
                <Badge className={`text-xs border ${advisory.recovery_allowed ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
                  {advisory.recovery_allowed ? "allowed" : "blocked"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Decision: <span className="font-medium">{advisory.decision || "—"}</span>
              </p>
              {advisory.reason && (
                <p className="text-xs text-muted-foreground">Reason: {advisory.reason}</p>
              )}
            </CardContent>
          </Card>
        )
      }
    </div>
  );
}
